import { Form, Button, Modal } from "antd";
import { FormInstance } from "antd/lib/form/util";
import React, { useState, useEffect } from "react";
import { TableAPI } from "../../API/TableAPI";
import Entry from "../../Model/Entry";

/** Props for modals used to create entries */
export interface CreateEntryModalProps {
  /** Is this modal for creating or editing an entry */
  mode: "create";

  /** Is the modal visible? */
  visible: boolean;
  /** Session ID for authorization */
  /** Called to hide the modal */
  onHide(refresh: boolean): void;
}

/** Props for modals used to edit entries */
export interface EditEntryModalProps {
  /** Is this modal for creating or editing an entry */
  mode: "edit";
  /** Entry to edit. If mode is "create" this must not be undefined */
  entry: Entry;

  /** Is the modal visible? */
  visible: boolean;
  /** Called to hide the modal */
  onHide(refresh: boolean): void;
}

/**
 * Create/Edit entry modal props
 * @see CreateEntryModalProps
 * @see EditEntryModalProps
 */
export type EntryModalProps = CreateEntryModalProps | EditEntryModalProps;

/**
 * This function creates a functional React component for displaying
 * "New Entry" modals. The modal will read all form's fields on submission,
 * check if there are any errors and then use the given API instance to
 * submit to the server.
 *
 * @param modalTitle The modal's title, a prefix is added (ex. 'Create' for mode=create)
 * @param api TableAPI to use. You can pass the table's instance
 * @param renderForm Function to render the form items, it should return a list of <Form.Item>
 * @param formToEntry Function to read the form's fields and return the values as an Entry
 * @param entryToForm Function to populate the form from an Entry. Used for editing
 * @see Entry
 */
export function makeEntryModal(
  modalTitle: string,
  api: TableAPI,
  renderForm: (form: FormInstance) => JSX.Element,
  formToEntry: (form: FormInstance) => Entry,
  entryToForm: (form: FormInstance, entry: Entry) => void
): React.FunctionComponent<EntryModalProps> {
  return (props: EntryModalProps) => {
    // State for visiblity and loading
    const [isLoading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Grab form from antd
    const [form] = Form.useForm();

    // If this is an edit modal and the entry changes, fill form
    const entry = props.mode === "edit" ? props.entry : null;
    useEffect(() => {
      if (entry !== null) {
        entryToForm(form, entry);
      }
    }, [form, entry]);

    // Form submission
    const submitForm = async () => {
      // Get all values from the form into an entry
      const entry = formToEntry(form);

      // Send request and handle response
      setLoading(true);
      let response;
      if (props.mode === "create") {
        response = await api.create(entry);
      } else {
        response = await api.update(props.entry["id"], entry);
      }

      setLoading(false);
      if (response.success) {
        props.onHide(true);
      } else {
        console.error("Could not submit form!");
        console.info("Response:");
        console.info(response.errorBody);
        setErrorMessage("Could not submit form!");
      }
    };

    // Callbacks
    /** Called when X or cancel is pressed to hide the modal */
    const onCancel = () => {
      form.resetFields();
      props.onHide(false);
    };

    /** Called when OK is pressed to submit the form */
    const onOk = async () => {
      try {
        await form.validateFields();
        submitForm();
      } catch (ex) {
        console.log("Form validation failed!");
      }
    };

    // Create footer
    const footer = [];
    if (errorMessage !== null) {
      footer.push(
        <span key="error" className="modal-error">
          {errorMessage}
        </span>
      );
    }
    footer.push(
      <Button key="back-button" onClick={onCancel}>
        Cancel
      </Button>,
      <Button key="ok-button" onClick={onOk} loading={isLoading}>
        Submit
      </Button>
    );

    return (
      <Modal
        visible={props.visible}
        bodyStyle={{ height: "600px", overflowY: "scroll" }}
        title={
          props.mode === "create"
            ? `Create ${modalTitle}`
            : `Edit ${modalTitle}`
        }
        footer={footer}
        onOk={onOk}
        onCancel={onCancel}
        centered
        forceRender
      >
        {renderForm(form)}
      </Modal>
    );
  };
}

export default makeEntryModal;
